import { type FC, type PropsWithChildren, type ReactNode } from 'react'
import classNames from 'classnames'
import { Roles } from '../../../../../components/Roles/Roles'
import { type HostPerson, hostDisplayName } from './hostPeople'
import styles from './BookingPages.module.scss'

interface WarningPillProps {
  className?: string
  children: ReactNode
}

export const WarningPill: FC<WarningPillProps> = ({ className, children }) => (
  <span
    className={classNames(
      'd-inline-flex align-items-center zocks-gap-0.375 py-1 ps-2 rounded-2 zocks-bg-color-warning text-warning-emphasis text-wrap text-start',
      styles.warningPill,
      className,
    )}
  >
    <i className='bi bi-exclamation-circle' aria-hidden='true' />
    <span>{children}</span>
  </span>
)

const RoleName: FC<PropsWithChildren> = ({ children }) => <span className='text-nowrap'>{children}</span>

interface HostStatusProps {
  host: HostPerson
}

const HostStatus: FC<HostStatusProps> = ({ host }) => {
  if (host.status !== 'active') {
    return (
      <span className='paragraph-small py-1 px-2 rounded-3 zocks-bg-color-gray-200 zocks-text-gray-600 text-nowrap text-capitalize'>
        {host.status ?? 'inactive'}
      </span>
    )
  }
  return host.eligible ? null : (
    <span className='paragraph-small py-1 px-2 rounded-3 zocks-bg-color-warning zocks-text-gray-900 text-nowrap'>Calendar not connected</span>
  )
}

interface HostRowProps {
  host: HostPerson
  displayName?: string
  isHost?: boolean
  leading?: ReactNode
  action?: ReactNode
}

export const HostRow: FC<HostRowProps> = ({ host, displayName, isHost = false, leading, action }) => {
  const name = displayName ?? hostDisplayName(host)
  return (
    <div
      role='listitem'
      className={classNames(
        'd-flex flex-wrap flex-sm-nowrap align-items-center zocks-py-0.75 column-gap-4 row-gap-2 border-bottom zocks-border-color-gray-300 flex-shrink-0',
        styles.hostRow,
      )}
    >
      {leading}
      <div className={classNames('d-flex flex-column zocks-gap-0.25 flex-grow-1', styles.hostIdentity)} title={host.email ? `${name} · ${host.email}` : name}>
        <span className={classNames('d-flex align-items-center zocks-gap-0.5', styles.hostNameLine)}>
          <span className={classNames('fw-bold text-truncate', styles.hostName)}>{name}</span>
          {isHost ? (
            <span className={classNames('paragraph-small px-2 rounded-3 border zocks-border-color-gray-300 zocks-text-gray-900 text-nowrap', styles.hostTag)}>
              Host
            </span>
          ) : null}
          <HostStatus host={host} />
        </span>
        <div className='d-flex align-items-center zocks-gap-0.375 paragraph-small zocks-text-gray-700 overflow-hidden'>
          {host.roleIds && host.roleIds.length > 0 ? (
            <>
              <Roles userRoleIds={host.roleIds} className='text-nowrap' roleElement={RoleName} />
              <span aria-hidden='true'>•</span>
            </>
          ) : null}
          {host.email ? <span className={classNames('text-truncate', styles.hostEmail)}>{host.email}</span> : null}
        </div>
      </div>
      {action ? <div className={classNames('d-flex align-items-center flex-shrink-0 ms-auto', styles.hostAction)}>{action}</div> : null}
    </div>
  )
}
